import React, { useState, useEffect } from 'react';
import { List, Divider, IconButton, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, isToday, isYesterday } from 'date-fns';
import es from 'date-fns/locale/es';
import en from 'date-fns/locale/en-US';
import MessageCard from './MessageCard';

import { supabase } from '../../supabaseClient';

const MessageList = ({ companyId, canEdit }) => {
  const [messages, setMessages] = useState({});
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('company', companyId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setMessages(categorizeMessages(data));
    } catch (error) {
      console.error('Error fetching messages:', error.message);
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);

      if (error) {
        throw error;
      }

      // Remove the deleted message from the messages state
      const newMessages = messages.filter((message) => message.id != messageId);
      setMessages(newMessages);
    } catch (error) {
      console.error('Error deleting message:', error.message);
    } finally {
      closeConfirmDelete();
    }
  };

  const openConfirmDelete = (message) => {
    setSelectedMessage(message);
    setConfirmDeleteOpen(true);
  };

  const closeConfirmDelete = () => {
    setSelectedMessage(null);
    setConfirmDeleteOpen(false);
  };

  const categorizeMessages = (messages) => {
    const categorizedMessages = {};
    const locale = companyId === 45 ? en : es;

    messages.forEach((message) => {
      const date = new Date(message.created_at);

      if (isToday(date)) {
        const category = companyId === 45 ? 'Today' : 'Hoy';
        if (!categorizedMessages[category]) {
          categorizedMessages[category] = [];
        }
        categorizedMessages[category].push(message);
      } else if (isYesterday(date)) {
        const category = companyId === 45 ? 'Yesterday' : 'Ayer';
        if (!categorizedMessages[category]) {
          categorizedMessages[category] = [];
        }
        categorizedMessages[category].push(message);
      } else {
        const category = format(date, 'dd MMMM', { locale });
        if (!categorizedMessages[category]) {
          categorizedMessages[category] = [];
        }
        categorizedMessages[category].push(message);
      }
    });

    return categorizedMessages;
  };

  useEffect(() => {
    fetchMessages();
  }, [companyId]);

  if (Object.keys(messages).length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {companyId === 45 ? "You don't have any messages yet." : "Aún no tienes mensajes."}
      </Alert>
    );
  }

  return (
    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
      {Object.keys(messages).map((category, index) => (
        <React.Fragment key={index}>
          <Typography variant="h6" align="center" sx={{ color: 'text.secondary', my: 2 }}>{category}</Typography>
          {messages[category].map((message, messageIndex) => (
            <React.Fragment key={messageIndex}>
              <Grid container alignItems="center" spacing={1}>

                <Grid item xs={canEdit ? 10 : 12}>
                  <MessageCard 
                    id={companyId} 
                    date={message.created_at} 
                    content={message.content} 
                    title={message.title} />
                </Grid>

                {canEdit && (
                  <Grid item xs={2}>
                    <IconButton
                      onClick={() => openConfirmDelete(message)}
                      aria-label="delete"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
              <Divider light />
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={closeConfirmDelete}>
        <DialogTitle>{companyId === 45 ? 'Confirm Deletion' : 'Confirmar Eliminación'}</DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <p>{companyId === 45 ? 'Are you sure you want to delete this message?' : '¿Estás seguro de que deseas eliminar el mensaje?'}</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDelete}>{companyId === 45 ? 'Cancel' : 'Cancelar'}</Button>
          <Button onClick={() => deleteMessage(selectedMessage?.id)} color="error">
            {companyId === 45 ? 'Delete' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </List>
  );
};

export default MessageList;